/**
 * Switch — an on/off control with a visible label (role="switch"). Use it
 * for a setting that takes effect at once or turns a section on; keep
 * checkboxes for picking items from a list.
 *
 * The label names the control and the optional description is announced as
 * its description, so screen readers hear "Added value policy, switch, on".
 */

import React, { useId } from 'react';

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Visible label; it is also the control's accessible name. */
  label: React.ReactNode;
  /** Helper text under the label. */
  description?: React.ReactNode;
  disabled?: boolean;
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
}

const DIMS = {
  sm: { width: 28, height: 16, knob: 12, font: 12.5 },
  md: { width: 34, height: 20, knob: 16, font: 13 },
} as const;

const Switch: React.FC<SwitchProps> = ({ checked, onChange, label, description, disabled = false, size = 'md', style }) => {
  const id = useId();
  const d = DIMS[size];
  const inset = (d.height - d.knob) / 2;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={`${id}-label`}
      aria-describedby={description ? `${id}-desc` : undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        textAlign: 'left',
        fontFamily: 'var(--font-sans)',
        color: 'var(--fg-2)',
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          flexShrink: 0,
          width: d.width,
          height: d.height,
          marginTop: 1,
          borderRadius: 'var(--radius-pill)',
          background: checked ? 'var(--accent)' : 'var(--cg-neutral-300)',
          transition: 'background 150ms var(--ease-standard)',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: inset,
            left: checked ? d.width - d.knob - inset : inset,
            width: d.knob,
            height: d.knob,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: 'var(--shadow-xs)',
            transition: 'left 150ms var(--ease-standard)',
          }}
        />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span id={`${id}-label`} style={{ fontSize: d.font, fontWeight: 600, color: 'var(--fg-1)' }}>
          {label}
        </span>
        {description && (
          <span id={`${id}-desc`} style={{ fontSize: 11.5, color: 'var(--fg-4)', fontWeight: 400 }}>
            {description}
          </span>
        )}
      </span>
    </button>
  );
};

export default Switch;
